import { App } from "vue";
import DataExportServiceClass from "../../../libs/application/data-export-service.js";
import HtmlToPdfUtilClass from "../../../src/shared/application/utils/html-to-pdf-util.js";
import { CustomDate } from "../../../src/shared/domain/classes/custom-date";
import header from "./asset-reports-html-content/html-header";
import footer from "./asset-reports-html-content/html-footer";

export default {
	install: (app: App, options?: Record<string, any>): void => {
		app.config.globalProperties.$download_file = function (
			file: Blob | File,
			file_name: string
		): void {
			const url = URL.createObjectURL(file);
			const a = document.createElement("a");
			a.href = url;
			a.download = file_name;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		};

		app.config.globalProperties.$download_file_from_url = async function (
			this: any,
			url: string,
			file_name: string
		): Promise<void> {
			const response = await fetch(url);
			const blob = await response.blob();
			this.$download_file(blob, file_name);
		};

		app.config.globalProperties.$get_xlsx_file = function (
			xlsx_content: string,
			file_name: string
		): File {
			// Helper function to convert string to ArrayBuffer safely
			function s2ab(s: string): ArrayBuffer {
				const buf = new ArrayBuffer(s.length);
				const view = new Uint8Array(buf);
				for (let i = 0; i < s.length; i++) {
					view[i] = s.charCodeAt(i) & 0xff;
				}
				return buf;
			}

			const blob = new Blob([s2ab(xlsx_content)], {
				type: "application/octet-stream"
			});

			return new File([blob], file_name, { type: blob.type });
		};

		app.config.globalProperties.$get_array_as_xlsx_file = function (
			this: any,
			array_content: any[][],
			file_name: string
		): File {
			const full_file_name = file_name + ".xlsx";

			const DataExportService = new DataExportServiceClass();
			const xlsx_content = DataExportService.convert_array_to_xlsx_content(
				array_content,
				full_file_name
			);

			const excel_file = this.$get_xlsx_file(xlsx_content, full_file_name);
			return excel_file;
		};

		app.config.globalProperties.$download_array_as_xlsx_file = function (
			this: any,
			array_content: any[][],
			file_name: string
		): void {
			const excel_file = this.$get_array_as_xlsx_file(array_content, file_name);
			this.$download_file(excel_file, file_name + ".xlsx");
		};

		app.config.globalProperties.$download_array_as_csv_file = function (
			this: any,
			array_content: any[][],
			file_name: string
		): void {
			let csv = "";
			array_content.forEach(function (row: any[]) {
				csv += '"';
				csv += row.join('","');
				csv += '"';
				csv += "\n";
			});

			const blob = new Blob([csv], { type: "text/csv" });
			this.$download_file(blob, file_name + ".csv");
		};

		app.config.globalProperties.$get_html_to_pdf_file_url = async function (
			html_content: string,
			file_name: string
		): Promise<any> {
			const HtmlToPdfUtil = new HtmlToPdfUtilClass();

			const pdf_file = await HtmlToPdfUtil.get_pdf_from_html(
				html_content,
				file_name
			);

			return pdf_file;
		};

		app.config.globalProperties.$get_asset_report_file_name = function (
			this: any,
			asset_report: any,
			asset: any
		): string | null {
			if (
				asset_report.type == "energy_measurements_tabular" &&
				asset_report.name
			) {
				return asset_report.name;
			} else if (!asset) return null;

			const asset_report_from = asset_report.date_from;
			const asset_report_to = asset_report.date_to;

			let result =
				asset.name + "_" + this.$format_date_to_month(asset_report_from);

			if (
				!new CustomDate(asset_report_from).is_same(
					new CustomDate(asset_report_to),
					"month"
				)
			) {
				result += "_" + this.$format_date_to_month(asset_report_to);
			}

			return result;
		};

		app.config.globalProperties.$download_asset_report_as_pdf_file =
			async function (
				this: any,
				html_content: any,
				file_name: string,
				custom_css_styles: string | null = null
			): Promise<void> {
				if (typeof html_content == "object" && html_content !== null) {
					html_content = html_content.innerHTML;
				}
				const formatted_file_name = file_name.replace(/[^a-zA-Z0-9-_]/g, "");
				const pdf_file = await this.$get_asset_report_as_pdf_file(
					html_content,
					formatted_file_name,
					custom_css_styles
				);

				this.$download_file(pdf_file, formatted_file_name);
			};

		app.config.globalProperties.$get_asset_report_as_pdf_file = async function (
			this: any,
			html_content: any,
			file_name: string,
			custom_css_styles: string | null = null
		): Promise<any> {
			if (typeof html_content == "object" && html_content !== null) {
				html_content = html_content.innerHTML;
			}
			const html_report = header(custom_css_styles) + html_content + footer;
			const pdf_file = await this.$get_html_to_pdf_file_url(
				html_report,
				file_name
			);

			return pdf_file;
		};
	}
};

// Declaring module augmentation to allow full IDE autocompletion inside Vue components
declare module "@vue/runtime-core" {
	interface ComponentCustomProperties {
		$download_file: (file: Blob | File, file_name: string) => void;
		$download_file_from_url: (url: string, file_name: string) => Promise<void>;
		$get_xlsx_file: (xlsx_content: string, file_name: string) => File;
		$get_array_as_xlsx_file: (
			array_content: any[][],
			file_name: string
		) => File;
		$download_array_as_xlsx_file: (
			array_content: any[][],
			file_name: string
		) => void;
		$download_array_as_csv_file: (
			array_content: any[][],
			file_name: string
		) => void;
		$get_html_to_pdf_file_url: (
			html_content: string,
			file_name: string
		) => Promise<any>;
		$get_asset_report_file_name: (
			asset_report: any,
			asset: any
		) => string | null;
		$download_asset_report_as_pdf_file: (
			html_content: any,
			file_name: string,
			custom_css_styles?: string | null
		) => Promise<void>;
		$get_asset_report_as_pdf_file: (
			html_content: any,
			file_name: string,
			custom_css_styles?: string | null
		) => Promise<any>;
	}
}
